import { useEffect, useState, type MouseEvent } from 'react'
import { AnimatePresence, motion, useScroll, useSpring } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'
import { useLenis } from 'lenis/react'
import { Menu, X } from 'lucide-react'
import { navItems, profile } from '@/data/site'
import { cn } from '@/lib/utils'

/**
 * Floating pill navbar with a sliding "tubelight" indicator under the active
 * section. On non-home routes, section links navigate back to the homepage.
 */
export function Navbar() {
  const { pathname } = useLocation()
  const isHome = pathname === '/'
  const lenis = useLenis()
  const [active, setActive] = useState<string>('')
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)

  const { scrollYProgress } = useScroll()
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 })

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!isHome) return
    const ids = ['top', ...navItems.map((n) => n.href.slice(1))]
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id)
        })
      },
      { rootMargin: '-45% 0px -50% 0px' },
    )
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [isHome])

  const go = (e: MouseEvent<HTMLAnchorElement>, href: string) => {
    setOpen(false)
    if (!isHome) return // let the browser navigate to /#section
    e.preventDefault()
    const target = href === '#top' ? 0 : (document.querySelector(href) as HTMLElement | null)
    if (target === null) return
    if (lenis) lenis.scrollTo(target, { offset: -88, duration: 1.2 })
    else if (target === 0) window.scrollTo({ top: 0, behavior: 'smooth' })
    else target.scrollIntoView({ behavior: 'smooth' })
    history.replaceState(null, '', href === '#top' ? '/' : href)
  }

  const hrefFor = (href: string) => (isHome ? href : `/${href}`)

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-4 pt-4">
      <motion.div
        aria-hidden="true"
        className="fixed inset-x-0 top-0 h-[2px] origin-left bg-accent-gradient"
        style={{ scaleX: progress }}
      />
      <nav
        aria-label="Primary"
        className={cn(
          'mx-auto flex max-w-5xl items-center justify-between gap-4 rounded-full border px-3 py-2 transition-all duration-500',
          scrolled || !isHome
            ? 'border-white/10 bg-ink/70 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.6)] backdrop-blur-xl'
            : 'border-transparent bg-transparent',
        )}
      >
        {isHome ? (
          <a
            href="#top"
            onClick={(e) => go(e, '#top')}
            aria-label={`${profile.name}, back to top`}
            className="group/brand flex items-center gap-3 rounded-full py-1 pl-1 pr-3"
          >
            <Brand hint="Back to top ↑" />
          </a>
        ) : (
          <Link
            to="/"
            aria-label={`${profile.name}, home`}
            className="group/brand flex items-center gap-3 rounded-full py-1 pl-1 pr-3"
          >
            <Brand hint="← Back home" />
          </Link>
        )}

        <ul className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => {
            const isActive = isHome && active === item.href.slice(1)
            return (
              <li key={item.href} className="relative">
                <a
                  href={hrefFor(item.href)}
                  onClick={(e) => go(e, item.href)}
                  aria-current={isActive ? 'true' : undefined}
                  className={cn(
                    'relative block rounded-full px-4 py-2 text-sm transition-colors',
                    isActive ? 'text-white' : 'text-zinc-400 hover:text-zinc-100',
                  )}
                >
                  {item.title}
                  {isActive && (
                    <motion.span
                      layoutId="nav-lamp"
                      className="absolute inset-0 -z-10 rounded-full bg-white/[0.06]"
                      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    >
                      <span className="absolute -top-2 left-1/2 h-1 w-8 -translate-x-1/2 rounded-t-full bg-lilac">
                        <span className="absolute -left-2 -top-2 h-6 w-12 rounded-full bg-lilac/25 blur-md" />
                        <span className="absolute -top-1 h-6 w-8 rounded-full bg-lilac/25 blur-md" />
                      </span>
                    </motion.span>
                  )}
                </a>
              </li>
            )
          })}
        </ul>

        <a
          href={hrefFor('#contact')}
          onClick={(e) => go(e, '#contact')}
          className="hidden h-9 items-center rounded-full bg-zinc-50 px-4 text-sm font-medium text-ink transition hover:bg-white md:inline-flex"
        >
          Let&apos;s talk
        </a>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-zinc-200 md:hidden"
        >
          {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="mx-auto mt-2 max-w-5xl rounded-3xl border border-white/10 bg-ink/90 p-2 backdrop-blur-xl md:hidden"
          >
            {navItems.map((item) => (
                <a
                  key={item.href}
                  href={hrefFor(item.href)}
                  onClick={(e) => go(e, item.href)}
                  className="block rounded-2xl px-4 py-3 text-base text-zinc-200 hover:bg-white/[0.05]"
                >
                  {item.title}
                </a>
              ))}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

const GHOST_PATH =
  'M230.809 115.385V249.411C230.809 269.923 214.985 287.282 194.495 288.411C184.544 288.949 175.364 285.718 168.26 280C159.746 273.154 147.769 273.461 139.178 280.23C132.638 285.384 124.381 288.462 115.379 288.462C106.377 288.462 98.1451 285.384 91.6055 280.23C82.912 273.385 70.9353 273.385 62.2415 280.23C55.7532 285.334 47.598 288.411 38.7246 288.462C17.4132 288.615 0 270.667 0 249.359V115.385C0 51.6667 51.6756 0 115.404 0C179.134 0 230.809 51.6667 230.809 115.385Z'

/**
 * Wordmark: a tiny version of the background ghost (rotating gradient,
 * blinking eyes that glance sideways on hover), the name set in two faces,
 * and a subtitle that rolls over to a hint on hover.
 */
function Brand({ hint }: { hint: string }) {
  return (
    <>
      <span className="relative grid h-9 w-9 shrink-0 place-items-center" aria-hidden="true">
        <span className="absolute inset-0 rounded-full bg-accent-gradient opacity-25 blur-md transition-opacity duration-500 group-hover/brand:opacity-60" />
        <svg
          viewBox="0 0 231 289"
          className="relative h-8 w-auto drop-shadow-[0_2px_8px_rgba(196,181,253,0.35)] transition-transform duration-500 ease-out group-hover/brand:-translate-y-0.5 group-hover/brand:-rotate-6"
        >
          <defs>
            <linearGradient id="brand-ghost" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ffb3d9" />
              <stop offset="50%" stopColor="#c4b5fd" />
              <stop offset="100%" stopColor="#87ceeb" />
              <animateTransform
                attributeName="gradientTransform"
                type="rotate"
                from="0 0.5 0.5"
                to="360 0.5 0.5"
                dur="8s"
                repeatCount="indefinite"
              />
            </linearGradient>
          </defs>
          <path d={GHOST_PATH} fill="url(#brand-ghost)" />
          <g className="transition-transform duration-300 ease-out group-hover/brand:translate-x-[18px]">
            <ellipse className="brand-eye" cx="80" cy="125" rx="20" ry="30" fill="#08070d" />
            <ellipse className="brand-eye" cx="150" cy="125" rx="20" ry="30" fill="#08070d" />
          </g>
        </svg>
      </span>

      <span className="flex flex-col gap-1 leading-none">
        <span className="text-[15px] font-medium tracking-tight text-zinc-100">
          Pujari{' '}
          <span className="text-gradient font-serif text-[17px] font-normal italic transition-[background-position] duration-700 group-hover/brand:[background-position:100%_0]">
            Keerthika
          </span>
        </span>
        <span className="relative block h-3 overflow-hidden font-mono text-[9.5px] uppercase tracking-[0.2em]">
          <span className="block text-zinc-500 transition-transform duration-300 ease-out group-hover/brand:-translate-y-full">
            AI · ML Researcher
          </span>
          <span className="absolute inset-x-0 top-full block text-lilac transition-transform duration-300 ease-out group-hover/brand:-translate-y-full">
            {hint}
          </span>
        </span>
      </span>
    </>
  )
}
